import { ReviewMinimized, ReviewRatingQuantity } from '../utils/types/Review'
import { REVIEWS_PER_PAGE, REVIEWS_PER_PAGE_AGGREGATION, REVIEWS_PER_PAGE_INITIAL } from '../utils/constants'
import { useApi } from './useApi'
import { getReviewFilterParams, getReviewUrlParams } from '../utils/reviewUtils'
import { createUrlParams } from '../lib/params'

interface ReviewFetch {
  reviews: ReviewMinimized[]
  ratingQuantities: ReviewRatingQuantity[]
  isLoading: boolean
  loadMoreReviews: () => void
}

const useReviewFetch = (
  productId: string,
): ReviewFetch => {
  const { getResource } = useApi()

  // Fetch rating quantities for the product using a single request
  const {
    data: ratingQuantitiesData = [],
    isLoading: isLoadingQuantities,
  } = getResource<ReviewRatingQuantity>(
    'review',
    createUrlParams({
      ...getReviewFilterParams(productId),
      page: {
        limit: REVIEWS_PER_PAGE_AGGREGATION,
      },
      other: {
        aggregate: 'review.rating',
      },
    }),
    {
      isInfinite: false,
    },
  )

  // Fetch the two initial reviews
  const {
    data: initialReviewsData = [],
    isLoading: isLoadingInitialReviews,
  } = getResource<ReviewMinimized>(
    'review',
    getReviewUrlParams(
      productId,
      Number(REVIEWS_PER_PAGE_INITIAL),
    ),
    {
      isInfinite: false,
    },
  )

  // Fetch rest of reviews using infinite fetch
  const {
    data: reviewsData = [],
    isLoading: isLoadingReviews,
    isLoadingMore,
    loadMore,
  } = getResource<ReviewMinimized>(
    'review',
    getReviewUrlParams(
      productId,
      Number(REVIEWS_PER_PAGE),
      Number(REVIEWS_PER_PAGE_INITIAL),
    ),
    {
      isInfinite: true,
      initialSize: 0,
    },
  )
  // Wrapper function ensures fresh definition of loadMore being called
  const loadMoreReviews = () => {
    loadMore()
  }

  const isLoading = isLoadingQuantities
    || isLoadingInitialReviews
    || isLoadingReviews
    || isLoadingMore

  const ratingQuantities = ratingQuantitiesData
  const reviews = [...initialReviewsData, ...reviewsData]

  return {
    reviews,
    ratingQuantities,
    isLoading,
    loadMoreReviews,
  }
}

export default useReviewFetch
